import { useEffect, useRef, lazy, Suspense } from "react";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import LogoBar from "@/components/LogoBar";
import ScrollReveal from "@/components/ScrollReveal";

// Lazy-load below-the-fold sections to reduce initial JS bundle
const WhatYouGetSection = lazy(() => import("@/components/WhatYouGetSection"));
const MetricsSection = lazy(() => import("@/components/MetricsSection"));
const FAQSection = lazy(() => import("@/components/FAQSection"));
const GoogleReviewsSection = lazy(() => import("@/components/GoogleReviewsSection"));
const CTASection = lazy(() => import("@/components/CTASection"));
const Footer = lazy(() => import("@/components/Footer"));
import { useFacebookPixel } from "@/hooks/useFacebookPixel";
import { useUserIp } from "@/hooks/useUserIp";

const Index = () => {
  const { trackViewContent } = useFacebookPixel();
  const userIp = useUserIp();
  const hasTracked = useRef(false);

  // Fire ViewContent event on landing page load - deferred to not block FCP
  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;

    const fire = () => trackViewContent({
      clientIpAddress: userIp || undefined,
      contentName: 'Viral Fox - Landing Page',
      contentCategory: 'landing_page',
    });

    // Defer to after first paint
    if ('requestIdleCallback' in window) {
      requestIdleCallback(fire);
    } else {
      setTimeout(fire, 500);
    }
  }, [trackViewContent, userIp]);

  return (
    <div className="min-h-screen bg-background text-foreground antialiased overflow-x-hidden">
      {/* Background Effects */}
      <div className="bg-grain"></div>
      <div className="bg-grid fixed inset-0 pointer-events-none"></div>

      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main>
        {/* Hero Section */}
        <HeroSection />

        {/* Logo Bar */}
        <ScrollReveal delay={100}>
          <LogoBar />
        </ScrollReveal>

        {/* What You Are Getting Section */}
        <Suspense fallback={null}>
          <ScrollReveal delay={0}>
            <WhatYouGetSection />
          </ScrollReveal>
        </Suspense>

        {/* Detailed Features / Metrics Section */}
        <Suspense fallback={null}>
          <ScrollReveal delay={0}>
            <MetricsSection />
          </ScrollReveal>
        </Suspense>

        {/* FAQ Section */}
        <Suspense fallback={null}>
          <ScrollReveal delay={0}>
            <FAQSection />
          </ScrollReveal>
        </Suspense>

        {/* Google Reviews Section */}
        <Suspense fallback={null}>
          <ScrollReveal delay={0}>
            <GoogleReviewsSection />
          </ScrollReveal>
        </Suspense>

        {/* CTA Section */}
        <Suspense fallback={null}>
          <ScrollReveal delay={0}>
            <CTASection />
          </ScrollReveal>
        </Suspense>
      </main>

      {/* Footer */}
      <Suspense fallback={null}>
        <ScrollReveal delay={0} direction="none">
          <Footer />
        </ScrollReveal>
      </Suspense>
    </div>
  );
};

export default Index;